import { useCropImage } from './hooks/useCropImage';
import { imageValidation } from './hooks/useFileValidation';
import { useFileUpload } from '@raysee/hooks';
import { VFC, useCallback, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';

type Props = {
  aspect: [number, number]; // 縦横比を指定します ((aspect[0]: width) / (aspect[1]: height))
  imageUrl: string;
  path: string;
  onUpload: (url: string) => void;
};

export const ImageCropUploader: VFC<Props> = (props) => {
  const { upload, error } = useFileUpload(props.path);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);

  const [imageSrc, setImageSrc] = useState<string>(props.imageUrl);
  useEffect(() => {
    setImageSrc(props.imageUrl);
  }, [props.imageUrl]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const onChangeZoom = (e: React.ChangeEvent<HTMLInputElement>) => setZoom(Number(e.target.value));
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onTargetFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result), false);
        reader.readAsDataURL(file);
      }).then((imageDataUrl) => {
        if (typeof imageDataUrl === 'string') {
          setImageSrc(imageDataUrl);
          setModalOpen(true);
        }
      });
    }
  };

  const onClickCropCancelButton = () => {
    setModalOpen(false);
    setImageSrc(props.imageUrl);
  };
  const onClickCropCompleteButton = async () => {
    setUploadLoading(true);
    try {
      const croppedImage = await useCropImage(imageSrc, croppedAreaPixels);
      setImageSrc(croppedImage);
      const cropImgFile = await fetch(croppedImage)
        .then((response) => response.blob())
        .then((blob) => new File([blob], 'image.jpeg', { type: 'image/jpeg' }));
      if (!imageValidation(cropImgFile)) return setImageSrc(props.imageUrl);
      const uploadedImageSrc = await upload(cropImgFile);
      if (!error) props.onUpload(uploadedImageSrc);
    } catch (e) {
      alert(e);
      console.error(e);
    } finally {
      setUploadLoading(false);
      setModalOpen(false);
    }
  };

  return (
    <>
      {imageSrc ? (
        <>
          <img className="pb-4" src={imageSrc} alt="画像がありません。" style={{ width: 200 }} />
          <label className="bg-brand rounded-md px-6 py-3 font-bold text-white shadow">
            サムネイルを入れ替える
            <input
              className="hidden"
              type="file"
              onChange={onTargetFileChange}
              accept="image/*"
              onClick={onTargetFileChange}
            />
          </label>
        </>
      ) : (
        <div className="pt-4">
          <label className="bg-brand rounded-md px-6 py-3 font-bold text-white shadow">
            サムネイルを登録する
            <input
              className="hidden"
              type="file"
              onChange={onTargetFileChange}
              accept="image/*"
              onClick={onTargetFileChange}
            />
          </label>
        </div>
      )}

      {modalOpen && (
        <>
          <div className="fixed inset-0 z-50 m-auto flex max-w-lg items-center">
            <div className="mx-4 flex w-full flex-col rounded-lg bg-white shadow-lg">
              <div className="flex p-5">
                <h3 className="text-xl font-semibold">サムネイルを設定する</h3>
              </div>
              <div className="relative h-72 flex-auto">
                <Cropper
                  aspect={props.aspect[0] / props.aspect[1]}
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="grid grid-cols-4 px-4 py-3">
                <div className="col-span-1 text-center">画像倍率</div>
                <input
                  className="col-span-3"
                  type="range"
                  min="1"
                  step="0.1"
                  max="5"
                  value={zoom}
                  onChange={(e) => onChangeZoom(e)}
                />
              </div>
              {uploadLoading ? (
                <div className="flex justify-center p-4">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-pink-500 border-t-transparent" />
                </div>
              ) : (
                <div className="flex justify-end p-4">
                  <button
                    className="px-7 font-bold text-red-500"
                    type="button"
                    onClick={onClickCropCancelButton}
                  >
                    キャンセル
                  </button>
                  <button
                    className="bg-brand rounded-md px-6 py-3 font-bold text-white shadow "
                    type="button"
                    onClick={onClickCropCompleteButton}
                  >
                    確定する
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="fixed inset-0 z-40 bg-black opacity-40" />
        </>
      )}
    </>
  );
};
